import { Box, FormControl } from '@mui/material';
import { Typography, colors } from '@sweep-io/sweep-design';
import { useSweepApi } from '../../apis/sweep';
import { useRunOnce } from '../common/useRunOnce';
import { useState } from 'react';
import { SearchSelect, SearchSelectItem } from '../common/search-select/SearchSelect';

interface AutomationNotifyEmailProps {
  readonly?: boolean;
  action: RunApexAutomationAction;
  onChange: (emails: RunApexAutomationAction) => any;
  crmOrgId: string;
}

interface ApexClassesResponse {
  className: string;
  methods: string[];
}

export const RunApexActionWrapper = ({
  readonly,
  action,
  onChange,
  crmOrgId,
}: AutomationNotifyEmailProps) => {
  const [apexData, setApexData] = useState<ApexClassesResponse[]>();
  const [isLoading, setIsLoading] = useState(true);
  const sweepApi = useSweepApi();

  const getApexData = async () => {
    const response = await sweepApi.get(`/crm-orgs/org/${crmOrgId}/apex-classes`);
    const data = response.data as ApexClassesResponse[];
    data?.sort((a, b) => a.className.localeCompare(b.className));
    return data;
  };

  useRunOnce(async () => {
    const data = await getApexData();
    setApexData(data);
    setIsLoading(false);
  });

  const apexClassItems: SearchSelectItem[] =
    apexData?.map((apexClass) => ({
      label: apexClass.className,
      value: apexClass.className,
    })) || [];

  const selectedClassMethods =
    apexData?.find((el) => el.className === action?.actionParams?.className)?.methods || [];

  const methodItems: SearchSelectItem[] = selectedClassMethods.map((method) => ({
    label: method,
    value: method,
  }));

  return (
    <>
      <Box
        sx={{
          background: colors.grey[100],
          padding: '24px 24px 19px',
          borderRadius: '8px',
          margin: '16px 0',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="body-medium">Invoke custom Apex</Typography>
        </Box>

        <FormControl
          sx={{
            minWidth: '120px',
            margin: '0 22px 16px 0',
          }}
        >
          <Box sx={{ display: 'flex', marginBottom: '8px', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: '65px' }}>
              <Typography variant="caption" color={colors.grey[800]}>
                Apex class:
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <SearchSelect
                items={apexClassItems}
                onChange={(item) => {
                  onChange({
                    ...action,
                    actionParams: {
                      ...action.actionParams,
                      className: item.value,
                      methodName: '', // Clear method when class changes
                    },
                  });
                }}
                SelectProps={{
                  disabled: readonly,
                  placeholder: 'Choose class',
                  value: action?.actionParams?.className ?? '',
                }}
                searchPlaceholder="Search classes..."
                isLoading={isLoading}
                emptyResultsMessage="No apex classes available"
              />
            </Box>
          </Box>
        </FormControl>
        <FormControl
          sx={{
            minWidth: '120px',
            margin: '0 22px 0px 0',
            display: 'flex',
            alignContent: 'center',
          }}
        >
          <Box sx={{ display: 'flex', marginBottom: '8px', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: '65px' }}>
              <Typography variant="caption" color={colors.grey[800]}>
                Method:
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <SearchSelect
                items={methodItems}
                onChange={(item) => {
                  onChange({
                    ...action,
                    actionParams: {
                      ...action.actionParams,
                      methodName: item.value,
                    },
                  });
                }}
                SelectProps={{
                  disabled: readonly || !action?.actionParams?.className,
                  placeholder: 'Choose static method',
                  value: action?.actionParams?.methodName ?? '',
                }}
                searchPlaceholder="Search methods..."
                isLoading={isLoading}
                emptyResultsMessage={
                  action?.actionParams?.className
                    ? 'No apex methods available'
                    : 'Select a class first'
                }
              />
            </Box>
          </Box>
        </FormControl>
      </Box>
    </>
  );
};
